// BiomassComponent.js
import React, { useEffect, useState, useRef } from 'react';

const BiomassComponent = ({
  layerConfig,
  mapInstance,
  id,
  isVisible,
  // Default to 100% if not provided
  opacity = 100,
}) => {
  const [tileUrls, setTileUrls] = useState({});
  const abortControllersRef = useRef({});

  // ---------------------------
  // 1) Helper to remove layers/sources from the map
  // ---------------------------
  const cleanupLayer = (layerId) => {
    if (!mapInstance) return;
    if (mapInstance.getLayer(layerId)) {
      mapInstance.removeLayer(layerId);
    }
    if (mapInstance.getSource(layerId)) {
      mapInstance.removeSource(layerId);
    }
  };

  // ---------------------------
  // 2) Helper to create a sub-layer
  // ---------------------------
  const setupLayer = async (subLayer) => {
    if (!mapInstance) return;

    // Abort any previous in-flight request for this subLayer
    if (abortControllersRef.current[subLayer.id]) {
      abortControllersRef.current[subLayer.id].abort();
    }
    const controller = new AbortController();
    abortControllersRef.current[subLayer.id] = controller;

    // Remove any existing layer/source for this subLayer
    cleanupLayer(subLayer.id);

    try {
      // Fetch the tile URL if getMapId is defined
      let tileUrl = '';
      if (typeof subLayer.getMapId === 'function') {
        tileUrl = await subLayer.getMapId(controller.signal);
      }
      if (!tileUrl) {
        // Fallback if no getMapId
        tileUrl = subLayer.source?.tiles?.[0] || '';
      }

      // Add the raster source
      mapInstance.addSource(subLayer.id, {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 256,
      });

      // Add the raster layer with initial opacity
      mapInstance.addLayer({
        id: subLayer.id,
        type: 'raster',
        source: subLayer.id,
        layout: {
          visibility: subLayer.isVisible ? 'visible' : 'none',
        },
        paint: {
          'raster-opacity': opacity / 100, // initial opacity
        },
      });

      // Store the tile URL (for debugging or display)
      setTileUrls((prev) => ({
        ...prev,
        [subLayer.id]: tileUrl,
      }));
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log(`Biomass: request aborted for ${subLayer.id}`);
      } else {
        console.error(
          `Biomass: failed to setup subLayer ${subLayer.id}`,
          err
        );
      }
    } finally {
      delete abortControllersRef.current[subLayer.id];
    }
  };

  // ---------------------------
  // 3) Effect A: Add/Remove sub-layers ONLY when map/visibility changes
  // ---------------------------
 useEffect(() => {
  // Effect A: only do "setupLayer" if the sub-layers or overall visibility changed
  // Do NOT depend on `opacity`.
  if (!mapInstance || !layerConfig?.subLayers) return;

  const setupLayersOnce = () => {
    if (!isVisible) {
      layerConfig.subLayers.forEach((sub) => cleanupLayer(sub.id));
      return;
    }
    // Add/fetch each sub-layer
    layerConfig.subLayers.forEach((sub) => setupLayer(sub));
  };

  if (mapInstance.isStyleLoaded()) {
    setupLayersOnce();
  } else {
    mapInstance.once('load', setupLayersOnce);
  }

  return () => {
    // Cleanup
    layerConfig.subLayers.forEach((sub) => cleanupLayer(sub.id));
  };
  // Only run if subLayers or isVisible changes
}, [mapInstance, layerConfig?.subLayers, isVisible]);

useEffect(() => {
  // Effect B: just update paint property when `opacity` changes
  if (!mapInstance || !layerConfig?.subLayers) return;

  layerConfig.subLayers.forEach((sub) => {
    if (mapInstance.getLayer(sub.id)) {
      mapInstance.setPaintProperty(
        sub.id,
        'raster-opacity',
        opacity / 100
      );
    }
  });
}, [mapInstance, layerConfig?.subLayers, opacity]);


  // Component doesn't render anything visible — it just attaches layers
  return null;
};

export default BiomassComponent;
